import { Component, inject, signal } from "@angular/core";
import { CommonModule } from "@angular/common";
import { Router } from "@angular/router";
import { ApiService } from "../core/api.service";
import { AuthService } from "../core/auth.service";

@Component({
  selector: "app-paywall",
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="paywall">
      <div class="paywall-card">
        <div class="paywall-mark">
          <img src="talos-foreman.png" alt="Talos Foreman" />
        </div>
        <h1>Your free trial has ended</h1>
        <p>
          Subscribe to keep using {{ auth.user()?.company_name || "your workspace" }} —
          projects, estimates, contracts, proposals, scheduling, the client portal,
          and your whole team.
        </p>

        <div *ngIf="error()" class="paywall-error">{{ error() }}</div>

        <button
          *ngIf="!isAdmin()"
          class="btn btn-primary paywall-btn"
          disabled
        >
          Ask your account owner to subscribe
        </button>

        <ng-container *ngIf="isAdmin()">
          <button
            class="btn btn-primary paywall-btn"
            [disabled]="busy()"
            (click)="hasSub() ? portal() : checkout()"
          >
            {{
              busy()
                ? "Redirecting…"
                : hasSub()
                  ? "Manage billing"
                  : "Subscribe to continue"
            }}
          </button>
        </ng-container>

        <button class="paywall-logout" (click)="logout()">Sign out</button>
      </div>
    </div>
  `,
  styles: [
    `
      .paywall {
        min-height: 100vh;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 24px;
        background: var(--bg, #f7f7f5);
      }
      .paywall-card {
        background: #fff;
        border: 1px solid var(--border, #e5e7eb);
        border-radius: 18px;
        max-width: 440px;
        width: 100%;
        padding: 40px 32px;
        text-align: center;
        box-shadow: 0 10px 40px rgba(0, 0, 0, 0.06);
      }
      .paywall-mark {
        width: 52px;
        height: 52px;
        border-radius: 14px;
        margin: 0 auto 18px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: #111827;
        color: #fff;
      }
      .paywall-mark svg {
        width: 26px;
        height: 26px;
      }
      .paywall-mark img {
        width: 75%;
        height: 75%;
        object-fit: contain;
      }
      .paywall-card h1 {
        font-size: 21px;
        font-weight: 800;
        margin: 0 0 10px;
      }
      .paywall-card p {
        color: var(--text-2, #6b7280);
        font-size: 14px;
        line-height: 1.6;
        margin: 0 0 22px;
      }
      .paywall-btn {
        width: 100%;
        margin-bottom: 10px;
      }
      .paywall-link {
        width: 100%;
      }
      .paywall-error {
        background: #fee2e2;
        color: #991b1b;
        border-radius: 10px;
        padding: 10px 12px;
        font-size: 13px;
        margin-bottom: 14px;
      }
      .paywall-logout {
        background: none;
        border: none;
        color: var(--text-3, #9ca3af);
        font-size: 13px;
        margin-top: 16px;
        cursor: pointer;
        text-decoration: underline;
      }
    `,
  ],
})
export class PaywallComponent {
  private api = inject(ApiService);
  private router = inject(Router);
  auth = inject(AuthService);

  busy = signal(false);
  error = signal<string | null>(null);
  hasSub = signal(false);

  isAdmin = this.auth.isAdmin;

  constructor() {
    // Learn whether a (lapsed) subscription exists, so admins see Manage vs Subscribe.
    this.api.getBilling().subscribe({
      next: (s) => this.hasSub.set(s.has_subscription),
      error: () => {},
    });
  }

  checkout() {
    this.busy.set(true);
    this.error.set(null);
    this.api.billingCheckout(window.location.origin).subscribe({
      next: (r) => (window.location.href = r.url),
      error: (e) => {
        this.error.set(e?.error?.detail || "Could not start checkout.");
        this.busy.set(false);
      },
    });
  }

  portal() {
    this.busy.set(true);
    this.error.set(null);
    this.api.billingPortal(window.location.origin).subscribe({
      next: (r) => (window.location.href = r.url),
      error: (e) => {
        this.error.set(e?.error?.detail || "Could not open the billing portal.");
        this.busy.set(false);
      },
    });
  }

  logout() {
    this.auth.logout().subscribe({
      next: () => this.router.navigate(["/login"]),
      error: () => this.router.navigate(["/login"]),
    });
  }
}
